<template>
  <div>
    <div class="toolbar">
      <b-button
        v-if="isHeadCounterOrManager"
        variant="primary"
        size="sm"
        :disabled="selectedIds.length === 0"
        @click="openMarkPaid"
      >
        <i class="fas fa-check"></i> ทำเครื่องหมายรับแล้ว ({{ selectedIds.length }})
      </b-button>
      <span v-else></span>
      <div class="toolbar-right">
        <b-form-checkbox v-if="startDate" v-model="showBeforeStart" switch size="sm" class="small" @change="load">
          แสดงรายการก่อน {{ formatDate(startDate) }}
        </b-form-checkbox>
        <strong class="small">{{ summaryText }}</strong>
        <b-button variant="outline-success" size="sm" aria-label="ส่งออก Excel" title="ส่งออก Excel" @click="exportExcel">
          <i class="fas fa-file-download"></i>
        </b-button>
      </div>
    </div>
    <b-alert :show="!!truncated" variant="warning" class="small py-2">{{ truncated }}</b-alert>
    <p v-if="showBeforeStart" class="small text-muted mb-2">
      รายการก่อน {{ formatDate(startDate) }} เป็นช่วงก่อนเริ่มใช้ระบบกระทบยอด ไม่นับในยอดค้างรับและรายงานลูกหนี้
      แต่ยังทำเครื่องหมายรับแล้วหรือจับคู่กับเอกสารได้ตามปกติ
    </p>

    <b-table
      hover
      small
      sort-icon-left
      head-variant="light"
      :items="filteredRows"
      :fields="fields"
      responsive
      show-empty
      empty-text="ไม่มีรายการค้างรับในช่วงนี้"
    >
      <template #head(box)>
        <b-form-checkbox
          v-if="isHeadCounterOrManager"
          :checked="isAllChecked"
          :indeterminate="isSomeChecked && !isAllChecked"
          aria-label="เลือกทั้งหมด"
          @change="toggleAll"
        />
      </template>
      <template #cell(box)="row">
        <b-form-checkbox v-if="isHeadCounterOrManager" v-model="row.item.box" :aria-label="`เลือก ${row.item.patientName}`" />
      </template>
      <template #cell(serviceDate)="row">
        {{ formatDate(row.item.serviceDate) }}
      </template>
      <template #cell(treatmentItems)="row">
        <span style="white-space: normal">{{ row.item.treatmentItems }}</span>
      </template>
      <template #cell(amount)="row">
        {{ formatAmount(row.item.amount) }}
      </template>
      <template #cell(age)="row">
        <span v-if="beforeStart(row.item)" class="recon-age old">ก่อนเริ่มใช้ระบบ</span>
        <span v-else class="recon-age" :class="{ warn: row.value > 45 }">{{ row.value }} วัน</span>
      </template>
      <template #custom-foot>
        <b-tr v-if="filteredRows.length" variant="secondary" class="font-weight-bold">
          <b-td :colspan="fields.length - 2">รวม {{ filteredRows.length.toLocaleString("th-TH") }} {{ unit }}</b-td>
          <b-td class="text-right">{{ formatAmount(visibleTotal) }}</b-td>
          <b-td></b-td>
        </b-tr>
      </template>
    </b-table>

    <MarkPaidModal ref="markPaidModal" :payer="payer" :claim-ids="selectedIds" @done="onDone" />
    <Loading v-if="isLoading" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  formatThaiShortDate, listSummaryText, truncationNote, ageDays, isBeforeStart,
} from "@/utils/reconciliation";
import { exportClaimsToExcel } from "@/utils/reconciliationExport";
import MarkPaidModal from "@/components/reconciliation/MarkPaidModal.vue";
import Loading from "@/components/Loading";

export default {
  name: "OutstandingTab",
  components: { MarkPaidModal, Loading },
  props: {
    payer: { type: String, required: true },
    // from PayerPanel: { dateFrom, dateTo, dateBasis, keyword, nonce } — ค้างรับ always uses the service date
    filter: { type: Object, required: true },
  },
  data() {
    return {
      // also list claims served before reconciliation went live, to reconcile them after the fact
      showBeforeStart: false,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("moduleReconciliation", ["getOutstanding", "getOutstandingMeta"]),
    ...mapGetters({ isAccessMenu: "moduleUser/getIsAccessMenu" }),
    ...mapGetters({ branchInfo: "moduleBranch/getBranchInfo", clinicInfo: "moduleBranch/getClinicInfo" }),
    // reconciliation.manage (mark-paid) is MANAGER/HEADCOUNTER only on the
    // backend; COUNTER only has reconciliation.view. Gate the mutate controls
    // here so a COUNTER sees a read-only tab instead of buttons that 403.
    isHeadCounterOrManager() {
      return this.isAccessMenu(["MANAGER", "HEADCOUNTER"]);
    },
    isNhso() {
      return this.payer === "NHSO";
    },
    unit() {
      return this.isNhso ? "visit" : "รายการ";
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    // NHSO rows are visits (flat 700, with doctor + treatments); SSO rows are receipt payments.
    fields() {
      return [
        { key: "box", label: "" },
        { key: "serviceDate", label: "วันที่ใช้สิทธิ", sortable: true },
        { key: "cid", label: "เลขบัตร ปชช.", sortable: true },
        { key: "patientName", label: "ชื่อ-สกุล", sortable: true },
        ...(this.isNhso ? [ { key: "doctorName", label: "แพทย์", sortable: true }, { key: "treatmentItems", label: "รายการรักษา" } ] : []),
        { key: "receiptNo", label: "เลขที่ใบเสร็จ", sortable: true },
        { key: "amount", label: this.isNhso ? "ยอดเบิก" : "จำนวนเงิน", sortable: true, tdClass: "text-right" },
        // computed per row (never written onto the Vuex rows); sortable by the computed value
        { key: "age", label: "ค้างมา", sortable: true, sortByFormatted: true, tdClass: "text-right", formatter: (v, k, item) => ageDays(item.serviceDate) },
      ];
    },
    filteredRows() {
      const k = (this.filter.keyword || "").toLowerCase();
      return this.getOutstanding.filter((r) => !k || `${r.cid || ""} ${r.patientName || ""} ${r.receiptNo || ""}`.toLowerCase().includes(k));
    },
    visibleTotal() {
      return this.filteredRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    },
    summaryText() {
      return listSummaryText(this.getOutstandingMeta?.pagination, this.getOutstandingMeta?.summary, this.unit);
    },
    // go-live day: ค้างรับ counts from here (from the API, so there is one source of truth)
    startDate() {
      return this.getOutstandingMeta?.startDate || null;
    },
    truncated() {
      return truncationNote(this.getOutstandingMeta?.pagination);
    },
    // Selection follows what is on screen: a ticked row hidden by a filter is not marked paid.
    selectedIds() {
      return this.filteredRows.filter((r) => r.box).map((r) => r.claimId);
    },
    isAllChecked() {
      return this.filteredRows.length > 0 && this.filteredRows.every((r) => r.box);
    },
    isSomeChecked() {
      return this.filteredRows.some((r) => r.box);
    },
  },
  watch: {
    payer() {
      this.load();
    },
    "filter.nonce"() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions("moduleReconciliation", ["fetchOutstanding"]),
    async load() {
      this.isLoading = true;
      try {
        // Rows arrive with `box: false` already declared by the setOutstanding
        // mutation, which is what makes the tick reactive — see the note there.
        // A fresh fetch therefore also clears any previous selection by design.
        await this.fetchOutstanding({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          payer: this.payer,
          dateFrom: this.filter.dateFrom,
          dateTo: this.filter.dateTo,
          includeBeforeStart: this.showBeforeStart,
        });
      } catch {
        // MarkPaidModal surfaces its own submit errors; keep the previous list on a load error.
      } finally {
        this.isLoading = false;
      }
    },
    toggleAll(checked) {
      this.filteredRows.forEach((r) => this.$set(r, "box", checked));
    },
    exportExcel() {
      exportClaimsToExcel({
        rows: this.filteredRows,
        payer: this.payer,
        status: "outstanding",
        dateFrom: this.filter.dateFrom,
        dateTo: this.filter.dateTo,
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
    },
    beforeStart(row) {
      return isBeforeStart(row.serviceDate, this.startDate);
    },
    formatDate(date) {
      return formatThaiShortDate(date);
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    openMarkPaid() {
      this.$refs.markPaidModal.show();
    },
    onDone() {
      this.$emit("paid");
      this.load();
    },
  },
};
</script>

<style scoped>
.recon-age {
  display: inline-block;
  min-width: 52px;
  text-align: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: #eef1f4;
  color: #4a5568;
  font-weight: 600;
}
.recon-age.warn {
  background: #fff4e0;
  color: #8a5300;
}
.recon-age.old {
  font-weight: 500;
  white-space: nowrap;
}
</style>
